import styled, { css } from 'styled-components';
import { flexColumn, flexCenterVertical } from '@stylesheets/helpers';
import { CODE_FONT_FAMILY } from '@stylesheets/font';

export const Wrapper = styled.div`
  ${flexColumn};

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level6} ${spacing.level6} 0;
    gap: ${spacing.level2};
  `}

  @media (max-width: 768px) {
    ${({ theme: { spacing } }) => css`
      padding: ${spacing.level5} ${spacing.level4} 0;
    `}
  }
`;

export const BalanceHeader = styled.div`
  ${flexColumn};
  align-items: flex-start;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level1};
    padding: ${spacing.level4} ${spacing.level5};
  `}
`;

export const BalanceAmount = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.displaySmall.fontSize};
    font-weight: ${font.displaySmall.fontWeight};
    line-height: ${font.displaySmall.lineHeight};
    font-family: ${font.displaySmall.fontFamily};
    color: ${colors.onSurface};
  `}
`;

export const BalanceUnit = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    line-height: ${font.bodyMedium.lineHeight};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const BatchSection = styled.div`
  ${flexColumn};

  ${({ theme: { colors, borderRadius } }) => css`
    background: ${colors.surfaceContainerLow};
    border-radius: ${borderRadius.large};
    overflow: hidden;
  `}
`;

export const BatchSectionLabel = styled.span`
  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.labelMedium.fontSize};
    font-weight: ${font.labelMedium.fontWeight};
    letter-spacing: 0.04em;
    color: ${colors.onSurfaceVariant};
    padding: ${spacing.level3} ${spacing.level4} ${spacing.level2};
  `}
`;

export const BatchRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;

  ${({ theme: { spacing, colors } }) => css`
    gap: ${spacing.level2};
    padding: ${spacing.level3} ${spacing.level4};
    border-bottom: 1px solid ${colors.outlineVariant};

    &:last-child {
      border-bottom: none;
    }
  `}

  @media (max-width: 600px) {
    flex-direction: column;
    align-items: flex-start;

    ${({ theme: { spacing } }) => css`
      gap: ${spacing.level1};
    `}
  }
`;

export const BatchLeft = styled.div`
  ${flexColumn};

  ${({ theme: { spacing } }) => css`
    gap: 2px;
    min-width: 0;
    padding-right: ${spacing.level3};
  `}
`;

export const BatchType = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    font-weight: ${font.labelLarge.fontWeight};
    color: ${colors.onSurface};
  `}
`;

export const BatchDate = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.labelSmall.fontSize};
    line-height: ${font.labelSmall.lineHeight};
    color: ${colors.outline};
  `}
`;

export const BatchInfo = styled.span`
  text-align: right;
  flex-shrink: 0;

  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    line-height: ${font.bodySmall.lineHeight};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const PurchaseForm = styled.div`
  ${flexColumn};

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level4};
    padding: ${spacing.level2} ${spacing.level5};
  `}
`;

export const QuantityRow = styled.div`
  display: flex;
  align-items: center;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level4};
  `}

  @media (max-width: 480px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

export const QuantityLabel = styled.span`
  flex-shrink: 0;

  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const QuantityInput = styled.input`
  width: 120px;

  ${({ theme: { colors, font, spacing, borderRadius } }) => css`
    padding: ${spacing.level3} ${spacing.level4};
    border-radius: ${borderRadius.small};
    border: 1px solid ${colors.outline};
    background: transparent;
    color: ${colors.onSurface};
    font-size: ${font.bodyMedium.fontSize};
    font-family: ${CODE_FONT_FAMILY};

    &:focus {
      outline: none;
      border-color: ${colors.primary};
      box-shadow: 0 0 0 1px ${colors.primary};
    }

    @media (max-width: 480px) {
      width: 100%;
    }
  `}
`;

export const QuantityCost = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.titleMedium.fontSize};
    font-weight: ${font.titleMedium.fontWeight};
    font-family: ${CODE_FONT_FAMILY};
    color: ${colors.primary};
  `}
`;

export const TransactionTable = styled.table`
  width: 100%;
  border-collapse: collapse;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};

    th,
    td {
      padding: ${spacing.level3} ${spacing.level4};
      text-align: left;
      white-space: nowrap;
    }

    th {
      font-weight: ${font.labelMedium.fontWeight};
      color: ${colors.onSurfaceVariant};
      font-size: ${font.labelSmall.fontSize};
      text-transform: uppercase;
      letter-spacing: 0.06em;
      border-bottom: 1px solid ${colors.outlineVariant};
    }

    td {
      color: ${colors.onSurface};
      border-bottom: 1px solid ${colors.surfaceContainerHighest};
    }

    tbody tr:last-child td {
      border-bottom: none;
    }

    tbody tr:hover {
      background: ${colors.surfaceContainerLow};
    }
  `}
`;

export const PaginationRow = styled.div`
  ${flexCenterVertical};
  justify-content: center;

  ${({ theme: { spacing, font, colors } }) => css`
    gap: ${spacing.level4};
    padding: ${spacing.level4} 0;
    font-size: ${font.bodySmall.fontSize};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  ${({ theme: { spacing } }) => css`
    padding: ${spacing.level3} ${spacing.level4};
  `}
`;

export const StatusLabel = styled.span`
  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    color: ${colors.onSurface};
  `}
`;

export const StatusBadge = styled.span<{ $status?: string }>`
  display: inline-block;

  ${({ theme: { spacing, borderRadius, font, colors }, $status }) => css`
    padding: ${spacing.level1} ${spacing.level3};
    border-radius: ${borderRadius.full};
    font-size: ${font.labelSmall.fontSize};
    font-weight: ${font.labelSmall.fontWeight};
    letter-spacing: 0.04em;
    background: ${$status === 'ACTIVE' ? colors.primaryContainer : colors.errorContainer};
    color: ${$status === 'ACTIVE' ? colors.onPrimaryContainer : colors.onErrorContainer};
  `}
`;

export const CancellationBanner = styled.div`
  ${({ theme: { colors, borderRadius, spacing, font } }) => css`
    background: ${colors.errorContainer};
    color: ${colors.onErrorContainer};
    border-radius: ${borderRadius.large};
    padding: ${spacing.level4} ${spacing.level5};
    font-size: ${font.bodyMedium.fontSize};
    line-height: ${font.bodyMedium.lineHeight};
  `}
`;

export const WarningText = styled.p`
  margin: 0;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodySmall.fontSize};
    line-height: ${font.bodySmall.lineHeight};
    color: ${colors.error};
    padding: ${spacing.level2} ${spacing.level5};
  `}
`;

export const InfoText = styled.p`
  margin: 0;

  ${({ theme: { font, colors } }) => css`
    font-size: ${font.bodySmall.fontSize};
    line-height: ${font.bodySmall.lineHeight};
    color: ${colors.onSurfaceVariant};
  `}
`;

export const SectionDescription = styled.p`
  margin: 0;

  ${({ theme: { font, colors, spacing } }) => css`
    font-size: ${font.bodyMedium.fontSize};
    line-height: ${font.bodyMedium.lineHeight};
    color: ${colors.onSurfaceVariant};
    padding: ${spacing.level2} ${spacing.level5};

    a {
      color: ${colors.primary};
      text-decoration: none;
      font-weight: ${font.labelLarge.fontWeight};

      &:hover {
        text-decoration: underline;
        opacity: 0.85;
      }
    }
  `}
`;

export const ButtonRow = styled.div`
  display: flex;

  ${({ theme: { spacing } }) => css`
    gap: ${spacing.level3};
    padding: ${spacing.level3} ${spacing.level5};
  `}
`;
